/// <reference types="vite-plugin-svgr/client" />
import { AviosCurrencySymbol, Box, Heading, Image, Paragraph } from '@alto-avios/alto-ui';
import { useVariableValue } from '@devcycle/react-client-sdk';
import { ComponentProps, FC } from 'react';

import BaLogo from '../../assets/ba-circle-logo.svg?react';
import BaLogoSquare from '../../assets/ba.png';
import { HeaderContent, useRequiredContent } from '../services/content';

interface AviosBalanceProps {
  balance: string;
  label?: string;
  logoWidth?: string;
  size?: ComponentProps<typeof Heading>['size'];
}

interface MyAccountProps {
  householdAviosBalance?: number;
  membershipNumber: string;
  personalAviosBalance?: number;
}

const AviosBalance: FC<AviosBalanceProps> = ({ balance, label, logoWidth = '24px', size = 'lg' }) => (
  <Box borderWidth="none" flexDirection="column" gap="4xs">
    <Paragraph foregroundColor="accentOnVibrant" size="xs">
      {label}
    </Paragraph>
    <Box alignItems="center" borderWidth="none" gap="5xs">
      <AviosCurrencySymbol kind="balance" styleVariant="white" width={logoWidth} />
      <Heading as="h6" foregroundColor="accentOnVibrant" size={size}>
        {balance}
      </Heading>
    </Box>
  </Box>
);

const renderBalance = (balance?: number) => balance?.toLocaleString('en-GB') ?? '-';

export const MyAccountDetailsCard = ({ householdAviosBalance, membershipNumber, personalAviosBalance }: MyAccountProps) => {
  const showHouseholdBalance = useVariableValue('show-household-balance', false);
  const { data } = useRequiredContent(HeaderContent);
  const { balanceLabel, householdBalanceLabel, membershipNumberLabel } = data.mobileAccount;

  return showHouseholdBalance ? (
    <Box backgroundColor="accentDeep" borderRadius="sm" borderWidth="none" flexDirection="column" gap="2xs" padding="lg">
      {householdAviosBalance ? (
        <Box borderWidth="none" flexDirection="column" gap="xs">
          <AviosBalance balance={renderBalance(householdAviosBalance)} label={householdBalanceLabel} />
          <AviosBalance balance={renderBalance(personalAviosBalance)} label={balanceLabel} logoWidth="18px" size="sm" />
        </Box>
      ) : (
        <AviosBalance balance={renderBalance(personalAviosBalance)} label={balanceLabel} />
      )}
      <Box borderWidth="none" gap="sm">
        <Box alignItems="center" borderWidth="none" justifyContent="center">
          {householdAviosBalance ? <Image alt="" borderRadius="4xs" height="40px" src={BaLogoSquare} width="40px" /> : <BaLogo />}
        </Box>
        <Box borderWidth="none" flexDirection="column" gap="4xs">
          <Paragraph foregroundColor="accentOnVibrant" size="xs">
            {membershipNumberLabel}
          </Paragraph>
          <Paragraph foregroundColor="accentOnDeep" size="sm">
            <b>{membershipNumber}</b>
          </Paragraph>
        </Box>
      </Box>
    </Box>
  ) : (
    <Box backgroundColor="accentDeep" borderRadius="sm" borderWidth="none" flexDirection="column" gap="2xs" padding="lg">
      <Box borderWidth="none" flexDirection="column" gap="4xs">
        <Paragraph foregroundColor="accentOnVibrant" size="xs">
          {balanceLabel}
        </Paragraph>
        <Heading as="h6" foregroundColor="accentOnVibrant" size="lg">
          {renderBalance(personalAviosBalance)}
        </Heading>
      </Box>
      <Box alignItems="center" borderWidth="none" gap="sm">
        <BaLogo />
        <Box borderWidth="none" flexDirection="column" gap="4xs">
          <Paragraph foregroundColor="accentOnVibrant" size="xs">
            {membershipNumberLabel}
          </Paragraph>
          <Paragraph foregroundColor="accentOnDeep" size="sm">
            <b>{membershipNumber}</b>
          </Paragraph>
        </Box>
      </Box>
    </Box>
  );
};
